import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import ResetPasswordModal from './ResetPasswordModal';

interface Props { isOpen: boolean; onClose: () => void; onOpenRegister: () => void; }

export default function SignInModal({ isOpen, onClose, onOpenRegister }: Props) {
  const { login } = useAuth();
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [showReset, setShowReset] = useState(false);
  const supabaseConfigured = Boolean(import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY);

  if (!isOpen) return null;

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      if (!supabaseConfigured) throw new Error('Supabase is not configured');
      await login({ type: 'email', credentials: { email, password } });
      onClose();
    } catch (err: any) {
      try { console.error('[auth] login failed', err); } catch {}
      const rawMessage = (err?.message || '').toString().toLowerCase();
      const status = typeof err?.status === 'number' ? err.status : undefined;
      if (rawMessage.includes('not configured')) {
        setError(t('auth.supabaseMissing'));
      } else if (rawMessage.includes('email not confirmed')) {
        setError(t('auth.errorEmailNotConfirmed'));
      } else if (rawMessage.includes('invalid login credentials')) {
        setError(t('auth.errorInvalidCredentials'));
      } else if (status === 429 || rawMessage.includes('rate limit')) {
        setError(t('auth.errorRateLimited'));
      } else if (rawMessage.includes('network') || rawMessage.includes('failed to fetch')) {
        setError(t('auth.errorNetwork'));
      } else {
        setError(t('auth.errorSignInDetailed'));
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex min-h-screen items-center justify-center bg-black/70 backdrop-blur-sm p-4 sm:p-6" role="dialog" aria-modal="true" aria-labelledby="signin-modal-title">
      <div className="w-full max-w-sm sm:max-w-md bg-gray-900 rounded-lg sm:rounded-xl shadow-2xl border border-pink-600/20 max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 sm:px-6 py-4 border-b border-gray-800">
          <h3 id="signin-modal-title" className="text-lg font-semibold text-white">{t('auth.signIn')}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white px-2 py-1 rounded">✕</button>
        </div>
        <div className="p-5 sm:p-6 space-y-4">
          <form onSubmit={handleEmailLogin} className="space-y-3">
            <label htmlFor="signin-email" className="block text-sm text-gray-300">{t('auth.email')}</label>
            <input id="signin-email" name="email" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} type="email" required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            <label htmlFor="signin-password" className="block text-sm text-gray-300">{t('auth.password')}</label>
            <input id="signin-password" name="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} type="password" required className="w-full rounded-md bg-black/40 border border-gray-700 px-3 py-2 text-white" />
            <button disabled={submitting} className="w-full bg-pink-600 hover:bg-pink-700 disabled:opacity-50 text-white font-semibold rounded-md py-2 mt-2">{t('auth.signIn')}</button>
            <button type="button" onClick={() => setShowReset(true)} className="w-full text-left text-xs text-pink-300 hover:text-pink-200 underline mt-1">{t('auth.forgotPassword')}</button>
          </form>
          {error && <p className="text-sm text-red-400">{error}</p>}
          <div className="text-xs text-gray-400">
            {t('auth.noAccount')} {" "}
            <button type="button" onClick={() => { onClose(); onOpenRegister(); }} className="underline text-pink-300 hover:text-pink-200">{t('auth.register')}</button>
          </div>
          {!supabaseConfigured && (
            <p className="text-xs text-yellow-400">{t('auth.supabaseMissing')}</p>
          )}
        </div>
      </div>
      <ResetPasswordModal isOpen={showReset} onClose={() => setShowReset(false)} />
    </div>
  );
}


